import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const BusinessYearSettings = ({ team, setTeam, saving, handleSubmit }) => {
  const [businessYear, setBusinessYear] = useState({
    businessYearStartMonth: 1,
    businessYearStartDay: 1,
    businessYearEndMonth: 12,
    businessYearEndDay: 31
  });

  // Month names for display
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Initialize form with team data
  useEffect(() => {
    if (team) {
      setBusinessYear({
        businessYearStartMonth: team.businessYearStartMonth || 1,
        businessYearStartDay: team.businessYearStartDay || 1,
        businessYearEndMonth: team.businessYearEndMonth || 12,
        businessYearEndDay: team.businessYearEndDay || 31
      });
    }
  }, [team]);

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numericValue = parseInt(value, 10);
    
    setBusinessYear(prev => ({
      ...prev,
      [name]: numericValue
    }));

    // Update parent team state
    setTeam(prev => ({
      ...prev,
      [name]: numericValue
    }));
  };

  // Get days in month for validation
  const getDaysInMonth = (month) => {
    // Handle February and months with different day counts
    const daysInMonth = {
      1: 31, 2: 29, 3: 31, 4: 30, 5: 31, 6: 30,
      7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31
    };
    return daysInMonth[month] || 31;
  };

  // Generate day options based on selected month
  const getDayOptions = (month) => {
    const maxDays = getDaysInMonth(month);
    return Array.from({ length: maxDays }, (_, i) => i + 1);
  };

  // Get business year display string
  const getBusinessYearDisplay = () => {
    const startMonth = months.find(m => m.value === businessYear.businessYearStartMonth)?.label || 'January';
    const endMonth = months.find(m => m.value === businessYear.businessYearEndMonth)?.label || 'December';
    
    return `${businessYear.businessYearStartDay} ${startMonth} - ${businessYear.businessYearEndDay} ${endMonth}`;
  };

  // Validate business year configuration
  const validateBusinessYear = () => {
    const startDate = new Date(2024, businessYear.businessYearStartMonth - 1, businessYear.businessYearStartDay);
    const endDate = new Date(2024, businessYear.businessYearEndMonth - 1, businessYear.businessYearEndDay);
    
    // Check if end date is after start date (accounting for year rollover)
    if (businessYear.businessYearEndMonth < businessYear.businessYearStartMonth) {
      // Year rollover is valid (e.g., April to March next year)
      return true;
    } else if (businessYear.businessYearEndMonth === businessYear.businessYearStartMonth) {
      // Same month - end day must be after start day
      return businessYear.businessYearEndDay > businessYear.businessYearStartDay;
    } else {
      // Different months in same year - always valid if end month > start month
      return true;
    }
  };

  // Handle form submission
  const onSubmit = (e) => {
    e.preventDefault();
    
    if (!validateBusinessYear()) {
      toast.error('Invalid business year configuration. End date must be after start date.');
      return;
    }
    
    handleSubmit(e);
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Business Year Configuration</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Set your organization's business year dates. Leave requests can only be made within the current business year period.
          </p>
        </div>
        
        <div className="px-6 py-4 space-y-6">
          {/* Current Business Year Display */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Current Business Year
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {getBusinessYearDisplay()}
                </p>
              </div>
            </div>
          </div>

          {/* Business Year Start */}
          <div>
            <h4 className="text-base font-medium text-gray-900 dark:text-white mb-4">Business Year Start</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="businessYearStartMonth" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Start Month
                </label>
                <select
                  id="businessYearStartMonth"
                  name="businessYearStartMonth"
                  value={businessYear.businessYearStartMonth}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                >
                  {months.map(month => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="businessYearStartDay" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Start Day
                </label>
                <select
                  id="businessYearStartDay"
                  name="businessYearStartDay"
                  value={businessYear.businessYearStartDay}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                >
                  {getDayOptions(businessYear.businessYearStartMonth).map(day => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Business Year End */}
          <div>
            <h4 className="text-base font-medium text-gray-900 dark:text-white mb-4">Business Year End</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="businessYearEndMonth" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  End Month
                </label>
                <select
                  id="businessYearEndMonth"
                  name="businessYearEndMonth"
                  value={businessYear.businessYearEndMonth}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                >
                  {months.map(month => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="businessYearEndDay" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  End Day
                </label>
                <select
                  id="businessYearEndDay"
                  name="businessYearEndDay"
                  value={businessYear.businessYearEndDay}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                >
                  {getDayOptions(businessYear.businessYearEndMonth).map(day => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Common Business Year Examples */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Common Business Year Configurations</h5>
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <div>• <strong>Calendar Year:</strong> 1 January - 31 December</div>
              <div>• <strong>UK Tax Year:</strong> 6 April - 5 April</div>
              <div>• <strong>US Federal:</strong> 1 October - 30 September</div>
              <div>• <strong>Academic Year:</strong> 1 September - 31 August</div>
            </div>
          </div>

          {/* Warning about changing business year */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Important Notice
                </h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  Changing the business year will affect leave booking restrictions and end-of-year processing. 
                  Existing leave requests outside the new business year period may need to be reviewed.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 text-right">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-primary-600 dark:hover:bg-primary-700"
          >
            {saving ? 'Saving...' : 'Save Business Year Settings'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default BusinessYearSettings;
